import { scoreMrzBand } from "./mrzBand";
import { isStableFrame } from "./stability";
import { detectBlur, BlurResult } from "./blur";
import { detectGlare, GlareResult } from "./glare";
import { analyzeExposure, ExposureResult } from "./exposure";
import { analyzeLocalContrast, LocalContrastResult } from "./localContrast";
import { scoreFrame, FrameScoreBreakdown } from "./scoreFrame";

export type { BlurResult, GlareResult, ExposureResult, LocalContrastResult };

export interface LiveQualityResult {
  // Legacy fields — used by the component's stability timer logic.
  brightnessScore: number;
  contrastScore: number;
  sharpnessScore: number;
  documentScore: number;
  mrzScore: number;
  stabilityScore: number;
  confidence: number;
  avgLum: number;
  frameDiff: number;

  // Phase 2 — real quality signals.
  blurVariance: number;
  isTooBlurred: boolean;
  glareScore: number;
  isGlareSevere: boolean;
  exposureScore: number;
  localContrastScore: number;
  mrzLocalContrast: number;
  hardReject: boolean;
  hardRejectReason?: string;
  scoreBreakdown: FrameScoreBreakdown;
}

export type GuidanceMessage =
  | "Center the passport"
  | "Move closer"
  | "Hold steady"
  | "Improve lighting"
  | "Reduce brightness"
  | "Avoid reflection"
  | "Align MRZ inside the frame"
  | "Show all four corners"
  | "Straighten the document"
  | "Ready";

export interface FrameAnalysisParams {
  pixels: Uint8ClampedArray;
  prevPixels: Uint8ClampedArray | null;
  guideW: number;
  guideH: number;
  midStart: number;
  midEnd: number;
  mrzFracs: number[];
  rollingMeanDiff: number;
  diffThreshold: number;
  rollingDiffThreshold: number;
}

export function analyzeFrame(p: FrameAnalysisParams): LiveQualityResult {
  const {
    pixels,
    prevPixels,
    guideW,
    guideH,
    midStart,
    midEnd,
    mrzFracs,
    rollingMeanDiff,
    diffThreshold,
    rollingDiffThreshold,
  } = p;

  const pixelCount = guideW * guideH;
  const lums = new Float32Array(pixelCount);
  const rowEdges = new Float32Array(guideH);

  // ── Pass 1: luminance + frame diff ───────────────────────────────────────
  let sumLum = 0,
    sumLumSq = 0,
    diffSum = 0,
    docCount = 0;
  const hasPrev = prevPixels !== null;

  for (let i = 0; i < pixels.length; i += 4) {
    const lum =
      (pixels[i] * 77 + pixels[i + 1] * 150 + pixels[i + 2] * 29) >> 8;
    lums[i >> 2] = lum;
    sumLum += lum;
    sumLumSq += lum * lum;
    if (lum > 85 && lum < 238) docCount++;
    if (hasPrev) {
      diffSum +=
        Math.abs(pixels[i] - prevPixels[i]) +
        Math.abs(pixels[i + 1] - prevPixels[i + 1]) +
        Math.abs(pixels[i + 2] - prevPixels[i + 2]);
    }
  }

  const avgLum = sumLum / pixelCount;
  const stddev = Math.sqrt(Math.max(0, sumLumSq / pixelCount - avgLum * avgLum));
  const docCoverage = docCount / pixelCount;
  const frameDiff = hasPrev ? diffSum / (pixelCount * 3) : 99;

  // ── Pass 2: horizontal edge density for MRZ band scoring ─────────────────
  const midRows = midEnd - midStart;
  let totalEdgeSum = 0,
    midEdgeSum = 0;
  for (let row = 0; row < guideH; row++) {
    const base = row * guideW;
    let rowEdge = 0;
    for (let col = 1; col < guideW; col++) {
      rowEdge += Math.abs(lums[base + col] - lums[base + col - 1]);
    }
    rowEdges[row] = rowEdge;
    totalEdgeSum += rowEdge;
    if (row >= midStart && row < midEnd) midEdgeSum += rowEdge;
  }

  const overallEdgeDensity = totalEdgeSum / (guideH * (guideW - 1));
  const midEdgeDensity = midEdgeSum / (midRows * (guideW - 1));
  const mrzScore = scoreMrzBand(rowEdges, guideH, guideW, mrzFracs, midEdgeDensity);

  // ── Real quality detection (Phase 2) ─────────────────────────────────────
  // mrzStartRow: top of the most-inclusive MRZ candidate band.
  const mrzStartRow = Math.floor(guideH * (1 - Math.max(...mrzFracs)));

  const blurResult = detectBlur(lums, guideW, guideH);
  const glareResult = detectGlare(lums, guideW, guideH, mrzStartRow);
  const exposureResult = analyzeExposure(lums);
  const localContrastResult = analyzeLocalContrast(lums, guideW, guideH, mrzStartRow);

  // ── Weighted scoring engine ───────────────────────────────────────────────
  const scoreBreakdown = scoreFrame({
    blurScore: blurResult.score,
    isTooBlurred: blurResult.isTooBlurred,
    glareScore: glareResult.score,
    isGlareSevere: glareResult.isSevere,
    exposureScore: exposureResult.score,
    contrastScore: localContrastResult.score,
    geometryScore: mrzScore,
  });

  // Document presence: large proportion of crop is paper-bright + has variation.
  const documentScore =
    Math.min(1, docCoverage / 0.4) * Math.min(1, stddev / 15);

  const stable = isStableFrame(
    frameDiff,
    rollingMeanDiff,
    diffThreshold,
    rollingDiffThreshold,
  );
  const stabilityScore = stable ? 1 : 0;

  return {
    // Legacy
    brightnessScore: exposureResult.score,
    contrastScore: localContrastResult.score,
    sharpnessScore: blurResult.score,
    documentScore,
    mrzScore,
    stabilityScore,
    confidence: scoreBreakdown.final,
    avgLum: exposureResult.meanLum,
    frameDiff,
    // Phase 2
    blurVariance: blurResult.variance,
    isTooBlurred: blurResult.isTooBlurred,
    glareScore: glareResult.score,
    isGlareSevere: glareResult.isSevere,
    exposureScore: exposureResult.score,
    localContrastScore: localContrastResult.score,
    mrzLocalContrast: localContrastResult.mrzLocalContrast,
    hardReject: scoreBreakdown.hardReject,
    hardRejectReason: scoreBreakdown.hardRejectReason,
    scoreBreakdown,
  };
}

export function deriveGuidance(
  result: LiveQualityResult,
  diffThreshold: number,
): GuidanceMessage {
  if (result.frameDiff > diffThreshold * 0.75) return "Hold steady";
  if (result.isTooBlurred) return "Hold steady";
  if (result.avgLum < 60) return "Improve lighting";
  if (result.avgLum > 220) return "Reduce brightness";
  if (result.isGlareSevere) return "Avoid reflection";
  if (result.documentScore < 0.25) return "Move closer";
  if (result.mrzScore < 0.35) return "Align MRZ inside the frame";
  return "Ready";
}

// ── Validation helper ─────────────────────────────────────────────────────────
// Used by validateCapturedImage to re-check quality on the final blob.

export interface ValidationQualityResult {
  blur: BlurResult;
  glare: GlareResult;
  exposure: ExposureResult;
  localContrast: LocalContrastResult;
}

export function validateFrameQuality(
  lums: Float32Array,
  w: number,
  h: number,
  mrzStartRow: number,
): ValidationQualityResult {
  return {
    blur: detectBlur(lums, w, h),
    glare: detectGlare(lums, w, h, mrzStartRow),
    exposure: analyzeExposure(lums),
    localContrast: analyzeLocalContrast(lums, w, h, mrzStartRow),
  };
}
